import { useCallback, useMemo } from "react";
import { useQueries, useQuery } from "react-query";
import { z } from "zod";
import { agent } from "~/util/agent";
import { useCounterpartyContextIds } from "../util";
import { useCounterparties } from "./counterparty";

const LedgerEntrySchema = z.object({
  id: z.string().uuid().min(1),
  reference_transaction_id: z.string().uuid().min(1),
  account_name: z.string(),
  account_type: z.string(),
  amount: z.string(),
  instrument: z.string(),
  entry_type: z.string(),
  entry_subtype: z.string(),
  entry_purpose: z.string(),
  date_effective: z.string(),
  default_account_balance: z.string(),
});

const LedgerEntriesSchema = z.record(z.string(), z.record(z.string(), z.array(LedgerEntrySchema)));

type LedgerEntriesResourceType = z.infer<typeof LedgerEntriesSchema>;
type LedgerEntryType = z.infer<typeof LedgerEntrySchema>;

const QUERY_LEDGER_ENTRIES_KEY = "ledger-entries";

const useLedgerEntries = (cpId?: string) => {
  return useQuery(
    [QUERY_LEDGER_ENTRIES_KEY, cpId],
    async () => agent(`accounting/entries?counterparty_id=${cpId}`).json<LedgerEntriesResourceType>(),
    {
      enabled: !!cpId,
    }
  );
};

const useOrgLedgerEntries = () => {
  const selectedCps = useCounterpartyContextIds();
  const { data = [], isError, isRefetching, refetch, isLoading } = useCounterparties();
  const result = useQueries(
    data
      .filter((d) => selectedCps.includes(d.id))
      .map((cp) => {
        return {
          queryKey: [QUERY_LEDGER_ENTRIES_KEY, cp.id],
          queryFn: async () => agent(`accounting/entries?counterparty_id=${cp.id}`).json<LedgerEntriesResourceType>(),
        };
      })
  );

  const refetchAll = useCallback(() => {
    refetch();
    result.forEach((result) => result.refetch());
  }, [refetch, result]);

  return useMemo(() => {
    return {
      isError: !!result.find((r) => r.isError) || isError,
      isLoading: !!result.find((r) => r.isLoading) || isLoading,
      isRefetching: !!result.find((r) => r.isRefetching) || isRefetching,
      refetch: refetchAll,
      data: result.flatMap((r) => r.data as unknown | null[]).filter(Boolean) as LedgerEntriesResourceType[],
      dataUpdatedAt: result.sort((a, b) => b.dataUpdatedAt - a.dataUpdatedAt)[0]?.dataUpdatedAt,
    };
  }, [isError, isLoading, isRefetching, refetchAll, result]);
};

export { LedgerEntriesSchema, useLedgerEntries, useOrgLedgerEntries };
export type { LedgerEntriesResourceType, LedgerEntryType };
